import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Star, Mic, BookOpen, Brain, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUEST_ICONS = {
  mic: Mic,
  book: BookOpen,
  cards: Brain,
  hospital: Heart,
  star: Star,
};

export function DailyQuests() {
  const [questData, setQuestData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchQuests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quests/daily`);
      setQuestData(res.data);
    } catch (err) {
      toast.error('Failed to load daily quests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuests();
  }, [fetchQuests]);

  if (loading) {
    return (
      <Card className="border-t-4 border-t-violet-500">
        <CardContent className="pt-6">
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={`quest-skeleton-${i}`} className="h-14 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!questData) return null;

  const quests = questData.quests || [];
  const completedCount = quests.filter(q => q.completed).length;
  const allDone = completedCount === quests.length;

  return (
    <Card className="border-t-4 border-t-violet-500" data-testid="daily-quests-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Star className="h-5 w-5 text-violet-500" />
            Daily Quests
          </CardTitle>
          <Badge variant={allDone ? "default" : "secondary"} className={allDone ? "bg-emerald-500" : ""}>
            {completedCount}/{quests.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {quests.map((quest) => {
          const IconComponent = QUEST_ICONS[quest.icon] || Star;
          const pct = Math.round((quest.progress / quest.target) * 100);

          return (
            <div
              key={quest.quest_id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                quest.completed
                  ? 'bg-emerald-500/5 border-emerald-500/30'
                  : 'bg-muted/30 border-border hover:border-violet-500/30'
              }`}
              data-testid={`quest-${quest.quest_id}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                quest.completed ? 'bg-emerald-500' : 'bg-violet-500/10'
              }`}>
                {quest.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-white" />
                ) : (
                  <IconComponent className="h-4 w-4 text-violet-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium truncate ${quest.completed ? 'line-through text-muted-foreground' : ''}`}>
                    {quest.title_en}
                  </span>
                  <span className="text-xs font-bold text-amber-500 shrink-0 ml-2">
                    +{quest.xp_reward} XP
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={pct} className="h-1.5 flex-1" />
                  <span className="text-xs text-muted-foreground shrink-0">
                    {quest.progress}/{quest.target}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {questData.total_xp_earned > 0 && (
          <div className="text-center pt-2 text-sm">
            <span className="text-amber-500 font-bold">+{questData.total_xp_earned} XP</span>
            <span className="text-muted-foreground"> earned today</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
